import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Listed by the Atlas-hosted MCP server on 2026-10-26, for an MCP configuration
// with GROUP_DATA_ACCESS_READ_ONLY; the list includes write tools. Its
// tools/list needs a token, which P0 does not hold when it syncs a gateway.
export const mongodbAtlasMcpTools: McpTool[] = [
  {
    name: "aggregate-db",
    description: "Run an aggregation against a MongoDB database",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        pipeline: {
          minItems: 1,
          type: "array",
          items: {
            type: "object",
            properties: {},
            additionalProperties: {},
          },
          description:
            "An array of aggregation stages to execute. The first stage must be a database-level aggregation stage (one of `$changeStream`, `$currentOp`, `$documents`, `$listLocalSessions`, `$queryStats`). https://www.mongodb.com/docs/manual/reference/mql/aggregation-stages/#db.aggregate---stages",
        },
        responseBytesLimit: {
          default: 1048576,
          description:
            "The maximum number of bytes to return in the response. This value is capped by the server's configured maximum and cannot be exceeded.",
          type: "number",
        },
      },
      required: ["connectionId", "database", "pipeline"],
      additionalProperties: false,
    },
    annotations: {
      title: "aggregate-db",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "aggregate",
    description: "Run an aggregation against a MongoDB collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        pipeline: {
          type: "array",
          items: {
            anyOf: [
              {
                type: "object",
                properties: {
                  $vectorSearch: {
                    anyOf: [
                      {
                        type: "object",
                        properties: {
                          exact: {
                            default: false,
                            description:
                              "When true, uses an ENN algorithm, otherwise uses ANN. Using ENN is not compatible with numCandidates, in that case, numCandidates must be left empty.",
                            type: "boolean",
                          },
                          index: {
                            type: "string",
                            description:
                              "Name of the index, as retrieved from the `collection-indexes` tool.",
                          },
                          path: {
                            type: "string",
                            description:
                              "Field, in dot notation, where to search. There must be a vector search index for that field.",
                          },
                          numCandidates: {
                            description:
                              "Number of candidates for the ANN algorithm. Mandatory when exact is false.",
                            type: "integer",
                            exclusiveMinimum: 0,
                            maximum: 9007199254740991,
                          },
                          limit: {
                            default: 10,
                            type: "integer",
                            exclusiveMinimum: 0,
                            maximum: 9007199254740991,
                          },
                          filter: {
                            description:
                              "MQL filter that can only use filter fields from the index definition. Note to LLM: If unsure, use the `collection-indexes` tool to learn which fields can be used for filtering.",
                            type: "object",
                            properties: {},
                            additionalProperties: {},
                          },
                          queryVector: {
                            type: "array",
                            items: {
                              type: "number",
                            },
                            description:
                              "The vector embeddings to search for when using classic vector search indexes (type: 'vector'). Provide embeddings as an array of numbers. Use this for classic vector indexes. For auto-embed indexes (type: 'autoEmbed'), use 'query' instead.",
                          },
                        },
                        required: ["index", "path", "queryVector"],
                        description:
                          "Classic vector search using 'queryVector'. Use this when the indexed field has a classic vector index (type: 'vector'). Note to LLM: Use the collection-indexes tool to verify the target field has a classic vector index before using 'queryVector'.",
                      },
                      {
                        type: "object",
                        properties: {
                          exact: {
                            default: false,
                            description:
                              "When true, uses an ENN algorithm, otherwise uses ANN. Using ENN is not compatible with numCandidates, in that case, numCandidates must be left empty.",
                            type: "boolean",
                          },
                          index: {
                            type: "string",
                            description:
                              "Name of the index, as retrieved from the `collection-indexes` tool.",
                          },
                          path: {
                            type: "string",
                            description:
                              "Field, in dot notation, where to search. There must be a vector search index for that field.",
                          },
                          numCandidates: {
                            description:
                              "Number of candidates for the ANN algorithm. Mandatory when exact is false.",
                            type: "integer",
                            exclusiveMinimum: 0,
                            maximum: 9007199254740991,
                          },
                          limit: {
                            default: 10,
                            type: "integer",
                            exclusiveMinimum: 0,
                            maximum: 9007199254740991,
                          },
                          filter: {
                            description:
                              "MQL filter that can only use filter fields from the index definition. Note to LLM: If unsure, use the `collection-indexes` tool to learn which fields can be used for filtering.",
                            type: "object",
                            properties: {},
                            additionalProperties: {},
                          },
                          query: {
                            type: "object",
                            properties: {
                              text: {
                                type: "string",
                                description: "The text query to search for.",
                              },
                            },
                            required: ["text"],
                            description:
                              "The query to search for when using auto-embed indexes (type: 'autoEmbed'). MongoDB will automatically generate embeddings for the text at query time. Use this for auto-embed indexes, not 'queryVector'.",
                          },
                          model: {
                            description:
                              "The embedding model to use for generating embeddings from the query text. If not specified, defaults to the model configured in the auto-embed index definition.",
                            type: "string",
                            enum: [
                              "voyage-4",
                              "voyage-4-large",
                              "voyage-4-lite",
                              "voyage-code-3",
                            ],
                          },
                        },
                        required: ["index", "path", "query"],
                        description:
                          "Auto-embed vector search using 'query'. Use this when the indexed field has an auto-embed index (type: 'autoEmbed'). Note to LLM: Use the collection-indexes tool to verify the target field has an auto-embed index before using 'query'.",
                      },
                    ],
                  },
                },
                required: ["$vectorSearch"],
              },
              {
                type: "object",
                properties: {},
                additionalProperties: {},
              },
            ],
          },
          description:
            "An array of aggregation stages to execute.\n\nIf the user has asked for a vector search, `$vectorSearch` **MUST** be the first stage\nof the pipeline (or the first stage of a `$unionWith` sub-pipeline only when explicitly\ncombining unrelated result sets — for hybrid full-text + vector search, use `$rankFusion`\nor `$scoreFusion` instead, see below).\n\nIf the user has asked for lexical/Atlas search, use `$search` instead of `$text`.\n### Usage Rules for `$vectorSearch`\n- **Index Type Detection:**\n  Use the collection-indexes tool to determine if the target field has a classic vector index (type: 'vector') or an auto-embed index (type: 'autoEmbed').\n- **Classic Vector Search (type: 'vector'):**\n  Use 'queryVector' with embeddings as an array of numbers.\n- **Auto-Embed Vector Search (type: 'autoEmbed'):**\n  Use 'query' - MongoDB automatically generates embeddings at query time. Do NOT use 'queryVector' or 'embeddingParameters' for auto-embed indexes.\n- **Unset embeddings:**\n  Unless the user explicitly requests the embeddings, add an `$unset` stage **at the end of the pipeline** to remove the embedding field and avoid context limits. **The $unset stage in this situation is mandatory**.\n- **Pre-filtering:**\n  If the user requests additional filtering, include filters in `$vectorSearch.filter` only for pre-filter fields in the vector index.\n  NEVER include fields in $vectorSearch.filter that are not part of the vector index.\n- **Post-filtering:**\n  For all remaining filters, add a $match stage after $vectorSearch.\n- If unsure which fields are filterable, use the collection-indexes tool to determine valid prefilter fields.\n- If no requested filters are valid prefilters, omit the filter key from $vectorSearch.\n\n### Usage Rules for `$search`\n- Include the index name, unless you know for a fact there's a default index. If unsure, use the collection-indexes tool to determine the index name.\n- The `$search` stage supports multiple operators, such as 'autocomplete', 'text', 'geoWithin', and others. Choose the appropriate operator based on the user's query. If unsure of the exact syntax, consult the MongoDB Atlas Search documentation, which can be found here: https://www.mongodb.com/docs/atlas/atlas-search/operators-and-collectors/\n\n### Usage Rules for `$rankFusion` and `$scoreFusion` (Hybrid Search)\nUse these stages when the user wants to combine full-text (`$search`) and vector\n(`$vectorSearch`) retrieval into a single fused result set. **Prefer native\nfusion over a `$unionWith` + `$group` workaround** — the workaround averages\nincompatible score scales and produces wrong rankings.\n\n**Which stage to use:**\n- `$rankFusion` (MongoDB 8.0+) — Reciprocal Rank Fusion. The recommended default.\n  Normalizes scores across incompatible scales automatically. No score tuning needed.\n- `$scoreFusion` (MongoDB 8.2+) — Score-based fusion. Use when the user needs explicit\n  per-pipeline weights, score normalisation (sigmoid / minMaxScaler), or a custom\n  combination expression.\n\n**Construction rules:**\n- `$rankFusion` / `$scoreFusion` MUST be the first stage of the top-level pipeline.\n- Sub-pipelines go inside `input.pipelines` as a named map (not an array). Each name\n  must be non-empty, must not start with `$`, and must not contain `.` or null bytes.\n- Allowed stages inside sub-pipelines: `$search`, `$vectorSearch`, `$match`, `$sort`,\n  `$geoNear`, `$skip`, `$limit`. `$project` and `$unset` are NOT allowed inside sub-pipelines.\n- Do field shaping (`$project` / `$unset`) only AFTER the fusion stage, at the root.\n- Both a vectorSearch (or autoEmbed) index AND a search (lexical) index must exist on\n  the collection. Use the collection-indexes tool to confirm both before running a hybrid query.\n- Add a `$limit` stage after the fusion stage to cap the final result set.\n- Add `$unset` at the end to remove embedding fields and avoid context bloat.\n\n### Usage Rules for `$rerank` (Native Reranking)\nUse this stage when the user wants to reorder a set of candidate documents using a cross-encoder reranker model.\n\n**Construction rules:**\n- `$rerank` can be any stage in the pipeline on an Atlas cluster running MongoDB 8.3 or higher.\n- It is recommended to use `$rerank` after a sorted pipeline, e.g. `$search`, `$vectorSearch`, `$rankFusion`, `$scoreFusion`, or [`$match`, `$sort`].\n- $rerank must be enabled via the Native Reranking Project Setting\n- Set `numDocsToRerank` as the number of documents passed into `$rerank`. This will also limit the number of documents returned by `$rerank`\n- Set `path` as a field name or an array of field names that exist in all documents. Use `$match` or `$set` before `$rerank` to validate no fields are missing.\n- Add `$addFields` after `$rerank` to retrieve the reranker score.\n\n**`$rerank` example (recommended default):**\n```javascript\n[\n  {\n    $match: {\n      description: { $exists: true },\n      name: { $exists: true }\n    }\n  },\n  {\n    $sort: {\n      lastUpdated: -1\n    }\n  },\n  {\n    $rerank: {\n      query: {\n        text: \"query text including instructions\"\n      },\n      model: \"rerank-2.5\",\n      numDocsToRerank: 100,\n      path: [\"description\", \"name\"]\n    }\n  },\n  {\n    $addFields: {\n      rerankScore: { $meta: \"score\" }\n    }\n  }\n]\n```\n",
        },
        responseBytesLimit: {
          default: 1048576,
          description:
            "The maximum number of bytes to return in the response. This value is capped by the server's configured maximum and cannot be exceeded.",
          type: "number",
        },
      },
      required: ["connectionId", "database", "collection", "pipeline"],
      additionalProperties: false,
    },
    annotations: {
      title: "aggregate",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "collection-indexes",
    description: "Describe the indexes for a collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "collection-indexes",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "collection-schema",
    description: "Describe the schema for a collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        sampleSize: {
          default: 50,
          description: "Number of documents to sample for schema inference",
          type: "number",
        },
        responseBytesLimit: {
          default: 1048576,
          description:
            "The maximum number of bytes to return in the response. This value is capped by the server's configured maximum and cannot be exceeded.",
          type: "number",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "collection-schema",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "collection-storage-size",
    description: "Gets the size of the collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "collection-storage-size",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "count",
    description:
      "Gets the number of documents in a MongoDB collection using db.collection.count() and query as an optional filter parameter",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        query: {
          description:
            "A filter/query parameter. Allows users to filter the documents to count. Matches the syntax of the filter argument of db.collection.count().",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "count",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "create-collection",
    description:
      "Creates a new collection in a database. If the database doesn't exist, it will be created automatically.",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "create-collection",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "create-index",
    description: "Create an index for a collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        name: {
          description: "The name of the index",
          type: "string",
        },
        definition: {
          type: "array",
          items: {
            oneOf: [
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "classic",
                  },
                  keys: {
                    type: "object",
                    propertyNames: {
                      type: "string",
                    },
                    additionalProperties: {
                      anyOf: [
                        {
                          type: "number",
                          const: 1,
                        },
                        {
                          type: "number",
                          const: -1,
                        },
                        {
                          type: "string",
                          const: "2d",
                        },
                        {
                          type: "string",
                          const: "2dsphere",
                        },
                        {
                          type: "string",
                          const: "text",
                        },
                        {
                          type: "string",
                          const: "geoHaystack",
                        },
                        {
                          type: "string",
                          const: "hashed",
                        },
                      ],
                      description: "The index key and its direction",
                    },
                    description: "The index definition",
                  },
                },
                required: ["type", "keys"],
                description:
                  "Definition for a MongoDB index (e.g. ascending/descending/geospatial/text).",
              },
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "vectorSearch",
                  },
                  fields: {
                    minItems: 1,
                    type: "array",
                    items: {
                      oneOf: [
                        {
                          type: "object",
                          properties: {
                            type: {
                              type: "string",
                              const: "filter",
                            },
                            path: {
                              type: "string",
                              description:
                                "Name of the field to index. For nested fields, use dot notation to specify path to embedded fields",
                            },
                          },
                          required: ["type", "path"],
                          additionalProperties: false,
                          description:
                            "Definition for a field that will be used for pre-filtering results.",
                        },
                        {
                          type: "object",
                          properties: {
                            type: {
                              type: "string",
                              const: "vector",
                            },
                            path: {
                              type: "string",
                              description:
                                "Name of the field containing vector embeddings as arrays of numbers. This must be a dedicated embeddings field, separate from any source text field, if any. For nested fields, use dot notation to specify path to embedded fields.",
                            },
                            numDimensions: {
                              default: 1024,
                              description:
                                "Number of vector dimensions that MongoDB Vector Search enforces at index-time and query-time",
                              type: "number",
                              minimum: 1,
                              maximum: 8192,
                            },
                            similarity: {
                              default: "euclidean",
                              description:
                                "Vector similarity function to use to search for top K-nearest neighbors. You can set this field only for vector-type fields.",
                              type: "string",
                              enum: ["cosine", "euclidean", "dotProduct"],
                            },
                            quantization: {
                              default: "none",
                              description:
                                "Type of automatic vector quantization for your vectors. Use this setting only if your embeddings are float or double vectors.",
                              type: "string",
                              enum: ["none", "scalar", "binary"],
                            },
                          },
                          required: ["type", "path"],
                          additionalProperties: false,
                          description:
                            "Definition for a field that contains vector embeddings. Use this type when the field you're indexing is supposed to store vector embeddings or when the user have specified one of the relevant parameters.",
                        },
                        {
                          type: "object",
                          properties: {
                            type: {
                              type: "string",
                              const: "autoEmbed",
                            },
                            path: {
                              type: "string",
                              description:
                                "Name of the field containing the source text from which embeddings will be automatically generated by MongoDB. This should be the text field itself, NOT a separate embeddings field containing the vector embeddings. For nested fields, use dot notation to specify the path to embedded fields.",
                            },
                            model: {
                              type: "string",
                              enum: [
                                "voyage-4",
                                "voyage-4-large",
                                "voyage-4-lite",
                                "voyage-code-3",
                              ],
                              description:
                                "Voyage embedding model to use for automatically generating embeddings from the source text in the indexed field.",
                            },
                            modality: {
                              type: "string",
                              enum: ["text"],
                              description:
                                "The data type / modality of the source content in the indexed field. Currently only 'text' is supported.",
                            },
                          },
                          required: ["type", "path", "model", "modality"],
                          additionalProperties: false,
                          description:
                            "Definition for a field containing source text from which embeddings will be automatically generated and managed by MongoDB at indexing time. Use this type when you want MongoDB to handle all embedding generation and storage internally. If the user specifies a dedicated field name for storing embeddings, use 'vector' type instead.",
                        },
                      ],
                    },
                    description:
                      "Definitions for the vector, autoEmbed and filter fields to index, one definition per document. Use 'vector' when user specifies a dedicated field that could contain vector embeddings. Use 'autoEmbed' when user wants MongoDB to automatically generate and manage embeddings internally from a text field (no separate embeddings field). Use 'filter' for additional fields to filter on. At least one 'vector' or 'autoEmbed' type field definition is required.",
                  },
                },
                required: ["type", "fields"],
                description: "Definition for a Vector Search index.",
              },
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "search",
                  },
                  analyzer: {
                    default: "lucene.standard",
                    description:
                      "The analyzer to use for the index. Can be one of the built-in lucene analyzers (`lucene.standard`, `lucene.simple`, `lucene.whitespace`, `lucene.keyword`), a language-specific analyzer, such as `lucene.cjk` or `lucene.czech`, or a custom analyzer defined by name in `analyzers`.",
                    type: "string",
                  },
                  mappings: {
                    type: "object",
                    properties: {
                      dynamic: {
                        default: false,
                        description:
                          "Enables or disables dynamic mapping of fields for this index. If set to true, Atlas Search recursively indexes all dynamically indexable fields. If set to false, you must specify individual fields to index using mappings.fields. Can also be set to `{ typeSet: '<name>' }` to reference a named entry in this index's `typeSets`, restricting dynamic mapping to only the field types listed in that type set.",
                        anyOf: [
                          {
                            type: "boolean",
                          },
                          {
                            type: "object",
                            properties: {
                              typeSet: {
                                type: "string",
                                description:
                                  "Name of a type set defined in this index's `typeSets`.",
                              },
                            },
                            required: ["typeSet"],
                            additionalProperties: false,
                          },
                        ],
                      },
                      fields: {
                        description:
                          "The field mapping definitions. If `dynamic` is set to `false`, this is required.",
                        type: "object",
                        propertyNames: {
                          type: "string",
                          description: "The field name",
                        },
                        additionalProperties: {
                          description:
                            "The field index definition. It must contain the field type (e.g. `{ type: 'string' }`), as well as any additional options for that field type (e.g. `analyzer`). Field types include `autocomplete`, `boolean`, `date`, `dateFacet`, `document`, `embeddedDocuments`, `geo`, `number`, `numberFacet`, `objectId`, `string`, `stringFacet`, `token`, `uuid`. Provide an array of field definitions instead of a single object to index the same field as multiple types, e.g. `[{ type: 'string' }, { type: 'autocomplete' }]`.",
                        },
                      },
                    },
                    additionalProperties: {},
                    description:
                      "Document describing the index to create. `fields` is required and must define at least one field when `dynamic` is `false` (the default). When `dynamic` is `true` or a typeSet object, `fields` may still be provided to index additional fields explicitly.",
                  },
                  numPartitions: {
                    description:
                      "Specifies the number of sub-indexes to create if the document count exceeds two billion. If omitted, defaults to 1.",
                    default: "1",
                    anyOf: [
                      {
                        type: "string",
                        const: "1",
                      },
                      {
                        type: "string",
                        const: "2",
                      },
                      {
                        type: "string",
                        const: "4",
                      },
                    ],
                  },
                },
                required: ["type", "mappings"],
                additionalProperties: {},
                description:
                  "Definition for an Atlas Search (lexical) index. Use this only if user explicitly asked for creating an Atlas search index or simply a search index.\n\nThis definition also accepts the following top-level fields, which are forwarded as-is and are not exhaustively typed here:\n- `analyzers`: array of custom analyzer definitions, e.g. `{ name: 'myAnalyzer', tokenizer: { type: 'standard' }, tokenFilters: [{ type: 'lowercase' }], charFilters: [{ type: 'htmlStrip' }] }`. Reference a custom analyzer by its `name` from a field's `analyzer`/`searchAnalyzer` option, instead of a built-in `lucene.*` analyzer. Tokenizer types include `standard`, `keyword`, `whitespace`, `nGram`, `edgeGram`, `regexSplit`, `regexCaptureGroup`, `uaxUrlEmail`. Token filter types include `lowercase`, `stopword`, `snowballStemming`, `asciiFolding`, `nGram`, `shingle`, `trim`, among others.\n- `synonyms`: array of synonym mappings, e.g. `{ name: 'mySynonyms', source: { collection: 'synonymsCollectionName' }, analyzer: 'lucene.standard' }`. The `analyzer` should match the analyzer used on the field(s) being searched.\n- `storedSource`: controls which fields are stored on search nodes for fast retrieval without a round trip to the database. `true` stores all fields, `false` (default) stores none, `{ include: ['fieldA', 'fieldB'] }` stores only the listed fields, `{ exclude: ['fieldC'] }` stores all except the listed fields.\n- `typeSets`: array of named field-type sets for use with `mappings.dynamic`, e.g. `{ name: 'myTypeSet', types: [{ type: 'string' }, { type: 'number' }] }`, to restrict dynamic mapping to only the listed types instead of every dynamically indexable type.\n",
              },
            ],
          },
          description:
            "The index definition. Use 'classic' for standard indexes, 'vectorSearch' for vector search indexes, and 'search' for Atlas Search (lexical) indexes.",
        },
      },
      required: ["connectionId", "database", "collection", "definition"],
      additionalProperties: false,
    },
    annotations: {
      title: "create-index",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "db-stats",
    description:
      "Returns statistics that reflect the use state of a single database",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
      },
      required: ["connectionId", "database"],
      additionalProperties: false,
    },
    annotations: {
      title: "db-stats",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "delete-many",
    description:
      "Removes all documents that match the filter from a MongoDB collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        filter: {
          description:
            "The query filter, specifying the deletion criteria. Matches the syntax of the filter argument of db.collection.deleteMany()",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "delete-many",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "drop-collection",
    description:
      "Removes a collection or view from the database. The method also removes any indexes associated with the dropped collection.",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "drop-collection",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "drop-database",
    description:
      "Removes the specified database, deleting the associated data files",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
      },
      required: ["connectionId", "database"],
      additionalProperties: false,
    },
    annotations: {
      title: "drop-database",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "drop-index",
    description: "Drop an index for the provided database and collection.",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        indexName: {
          type: "string",
          minLength: 1,
          description: "The name of the index to be dropped.",
        },
        type: {
          type: "string",
          enum: ["classic", "search"],
          description:
            "The type of index to be deleted. Use 'classic' for standard indexes and 'search' for atlas search and vector search indexes.",
        },
      },
      required: ["connectionId", "database", "collection", "indexName", "type"],
      additionalProperties: false,
    },
    annotations: {
      title: "drop-index",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "explain",
    description:
      "Returns statistics describing the execution of the winning plan chosen by the query optimizer for the evaluated method",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        method: {
          type: "array",
          items: {
            oneOf: [
              {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    const: "aggregate",
                  },
                  arguments: {
                    type: "object",
                    properties: {
                      pipeline: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                $vectorSearch: {
                                  anyOf: [
                                    {
                                      type: "object",
                                      properties: {
                                        exact: {
                                          default: false,
                                          description:
                                            "When true, uses an ENN algorithm, otherwise uses ANN. Using ENN is not compatible with numCandidates, in that case, numCandidates must be left empty.",
                                          type: "boolean",
                                        },
                                        index: {
                                          type: "string",
                                          description:
                                            "Name of the index, as retrieved from the `collection-indexes` tool.",
                                        },
                                        path: {
                                          type: "string",
                                          description:
                                            "Field, in dot notation, where to search. There must be a vector search index for that field.",
                                        },
                                        numCandidates: {
                                          description:
                                            "Number of candidates for the ANN algorithm. Mandatory when exact is false.",
                                          type: "integer",
                                          exclusiveMinimum: 0,
                                          maximum: 9007199254740991,
                                        },
                                        limit: {
                                          default: 10,
                                          type: "integer",
                                          exclusiveMinimum: 0,
                                          maximum: 9007199254740991,
                                        },
                                        filter: {
                                          description:
                                            "MQL filter that can only use filter fields from the index definition. Note to LLM: If unsure, use the `collection-indexes` tool to learn which fields can be used for filtering.",
                                          type: "object",
                                          properties: {},
                                          additionalProperties: {},
                                        },
                                        queryVector: {
                                          type: "array",
                                          items: {
                                            type: "number",
                                          },
                                          description:
                                            "The vector embeddings to search for when using classic vector search indexes (type: 'vector'). Provide embeddings as an array of numbers. Use this for classic vector indexes. For auto-embed indexes (type: 'autoEmbed'), use 'query' instead.",
                                        },
                                      },
                                      required: [
                                        "index",
                                        "path",
                                        "queryVector",
                                      ],
                                      description:
                                        "Classic vector search using 'queryVector'. Use this when the indexed field has a classic vector index (type: 'vector'). Note to LLM: Use the collection-indexes tool to verify the target field has a classic vector index before using 'queryVector'.",
                                    },
                                    {
                                      type: "object",
                                      properties: {
                                        exact: {
                                          default: false,
                                          description:
                                            "When true, uses an ENN algorithm, otherwise uses ANN. Using ENN is not compatible with numCandidates, in that case, numCandidates must be left empty.",
                                          type: "boolean",
                                        },
                                        index: {
                                          type: "string",
                                          description:
                                            "Name of the index, as retrieved from the `collection-indexes` tool.",
                                        },
                                        path: {
                                          type: "string",
                                          description:
                                            "Field, in dot notation, where to search. There must be a vector search index for that field.",
                                        },
                                        numCandidates: {
                                          description:
                                            "Number of candidates for the ANN algorithm. Mandatory when exact is false.",
                                          type: "integer",
                                          exclusiveMinimum: 0,
                                          maximum: 9007199254740991,
                                        },
                                        limit: {
                                          default: 10,
                                          type: "integer",
                                          exclusiveMinimum: 0,
                                          maximum: 9007199254740991,
                                        },
                                        filter: {
                                          description:
                                            "MQL filter that can only use filter fields from the index definition. Note to LLM: If unsure, use the `collection-indexes` tool to learn which fields can be used for filtering.",
                                          type: "object",
                                          properties: {},
                                          additionalProperties: {},
                                        },
                                        query: {
                                          type: "object",
                                          properties: {
                                            text: {
                                              type: "string",
                                              description:
                                                "The text query to search for.",
                                            },
                                          },
                                          required: ["text"],
                                          description:
                                            "The query to search for when using auto-embed indexes (type: 'autoEmbed'). MongoDB will automatically generate embeddings for the text at query time. Use this for auto-embed indexes, not 'queryVector'.",
                                        },
                                        model: {
                                          description:
                                            "The embedding model to use for generating embeddings from the query text. If not specified, defaults to the model configured in the auto-embed index definition.",
                                          type: "string",
                                          enum: [
                                            "voyage-4",
                                            "voyage-4-large",
                                            "voyage-4-lite",
                                            "voyage-code-3",
                                          ],
                                        },
                                      },
                                      required: ["index", "path", "query"],
                                      description:
                                        "Auto-embed vector search using 'query'. Use this when the indexed field has an auto-embed index (type: 'autoEmbed'). Note to LLM: Use the collection-indexes tool to verify the target field has an auto-embed index before using 'query'.",
                                    },
                                  ],
                                },
                              },
                              required: ["$vectorSearch"],
                            },
                            {
                              type: "object",
                              properties: {},
                              additionalProperties: {},
                            },
                          ],
                        },
                        description:
                          "An array of aggregation stages to execute.\n\nIf the user has asked for a vector search, `$vectorSearch` **MUST** be the first stage\nof the pipeline (or the first stage of a `$unionWith` sub-pipeline only when explicitly\ncombining unrelated result sets — for hybrid full-text + vector search, use `$rankFusion`\nor `$scoreFusion` instead, see below).\n\nIf the user has asked for lexical/Atlas search, use `$search` instead of `$text`.\n### Usage Rules for `$vectorSearch`\n- **Index Type Detection:**\n  Use the collection-indexes tool to determine if the target field has a classic vector index (type: 'vector') or an auto-embed index (type: 'autoEmbed').\n- **Classic Vector Search (type: 'vector'):**\n  Use 'queryVector' with embeddings as an array of numbers.\n- **Auto-Embed Vector Search (type: 'autoEmbed'):**\n  Use 'query' - MongoDB automatically generates embeddings at query time. Do NOT use 'queryVector' or 'embeddingParameters' for auto-embed indexes.\n- **Unset embeddings:**\n  Unless the user explicitly requests the embeddings, add an `$unset` stage **at the end of the pipeline** to remove the embedding field and avoid context limits. **The $unset stage in this situation is mandatory**.\n- **Pre-filtering:**\n  If the user requests additional filtering, include filters in `$vectorSearch.filter` only for pre-filter fields in the vector index.\n  NEVER include fields in $vectorSearch.filter that are not part of the vector index.\n- **Post-filtering:**\n  For all remaining filters, add a $match stage after $vectorSearch.\n- If unsure which fields are filterable, use the collection-indexes tool to determine valid prefilter fields.\n- If no requested filters are valid prefilters, omit the filter key from $vectorSearch.\n\n### Usage Rules for `$search`\n- Include the index name, unless you know for a fact there's a default index. If unsure, use the collection-indexes tool to determine the index name.\n- The `$search` stage supports multiple operators, such as 'autocomplete', 'text', 'geoWithin', and others. Choose the appropriate operator based on the user's query. If unsure of the exact syntax, consult the MongoDB Atlas Search documentation, which can be found here: https://www.mongodb.com/docs/atlas/atlas-search/operators-and-collectors/\n\n### Usage Rules for `$rankFusion` and `$scoreFusion` (Hybrid Search)\nUse these stages when the user wants to combine full-text (`$search`) and vector\n(`$vectorSearch`) retrieval into a single fused result set. **Prefer native\nfusion over a `$unionWith` + `$group` workaround** — the workaround averages\nincompatible score scales and produces wrong rankings.\n\n**Which stage to use:**\n- `$rankFusion` (MongoDB 8.0+) — Reciprocal Rank Fusion. The recommended default.\n  Normalizes scores across incompatible scales automatically. No score tuning needed.\n- `$scoreFusion` (MongoDB 8.2+) — Score-based fusion. Use when the user needs explicit\n  per-pipeline weights, score normalisation (sigmoid / minMaxScaler), or a custom\n  combination expression.\n\n**Construction rules:**\n- `$rankFusion` / `$scoreFusion` MUST be the first stage of the top-level pipeline.\n- Sub-pipelines go inside `input.pipelines` as a named map (not an array). Each name\n  must be non-empty, must not start with `$`, and must not contain `.` or null bytes.\n- Allowed stages inside sub-pipelines: `$search`, `$vectorSearch`, `$match`, `$sort`,\n  `$geoNear`, `$skip`, `$limit`. `$project` and `$unset` are NOT allowed inside sub-pipelines.\n- Do field shaping (`$project` / `$unset`) only AFTER the fusion stage, at the root.\n- Both a vectorSearch (or autoEmbed) index AND a search (lexical) index must exist on\n  the collection. Use the collection-indexes tool to confirm both before running a hybrid query.\n- Add a `$limit` stage after the fusion stage to cap the final result set.\n- Add `$unset` at the end to remove embedding fields and avoid context bloat.\n\n### Usage Rules for `$rerank` (Native Reranking)\nUse this stage when the user wants to reorder a set of candidate documents using a cross-encoder reranker model.\n\n**Construction rules:**\n- `$rerank` can be any stage in the pipeline on an Atlas cluster running MongoDB 8.3 or higher.\n- It is recommended to use `$rerank` after a sorted pipeline, e.g. `$search`, `$vectorSearch`, `$rankFusion`, `$scoreFusion`, or [`$match`, `$sort`].\n- $rerank must be enabled via the Native Reranking Project Setting\n- Set `numDocsToRerank` as the number of documents passed into `$rerank`. This will also limit the number of documents returned by `$rerank`\n- Set `path` as a field name or an array of field names that exist in all documents. Use `$match` or `$set` before `$rerank` to validate no fields are missing.\n- Add `$addFields` after `$rerank` to retrieve the reranker score.\n\n**`$rerank` example (recommended default):**\n```javascript\n[\n  {\n    $match: {\n      description: { $exists: true },\n      name: { $exists: true }\n    }\n  },\n  {\n    $sort: {\n      lastUpdated: -1\n    }\n  },\n  {\n    $rerank: {\n      query: {\n        text: \"query text including instructions\"\n      },\n      model: \"rerank-2.5\",\n      numDocsToRerank: 100,\n      path: [\"description\", \"name\"]\n    }\n  },\n  {\n    $addFields: {\n      rerankScore: { $meta: \"score\" }\n    }\n  }\n]\n```\n",
                      },
                    },
                    required: ["pipeline"],
                  },
                },
                required: ["name", "arguments"],
              },
              {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    const: "find",
                  },
                  arguments: {
                    type: "object",
                    properties: {
                      filter: {
                        description:
                          "The query filter, matching the syntax of the query argument of db.collection.find()",
                        type: "object",
                        properties: {},
                        additionalProperties: {},
                      },
                      projection: {
                        description:
                          "The projection, matching the syntax of the projection argument of db.collection.find()",
                        type: "object",
                        properties: {},
                        additionalProperties: {},
                      },
                      limit: {
                        default: 10,
                        description:
                          "The maximum number of documents to return",
                        type: "number",
                      },
                      sort: {
                        description:
                          "A document, describing the sort order, matching the syntax of the sort argument of cursor.sort(). The keys of the object are the fields to sort on, while the values are the sort directions (1 for ascending, -1 for descending).",
                        type: "object",
                        propertyNames: {
                          type: "string",
                        },
                        additionalProperties: {
                          anyOf: [
                            {
                              type: "number",
                              const: 1,
                            },
                            {
                              type: "number",
                              const: -1,
                            },
                            {
                              type: "string",
                              const: "asc",
                            },
                            {
                              type: "string",
                              const: "desc",
                            },
                            {
                              type: "string",
                              const: "ascending",
                            },
                            {
                              type: "string",
                              const: "descending",
                            },
                            {
                              type: "object",
                              properties: {
                                $meta: {
                                  type: "string",
                                },
                              },
                              required: ["$meta"],
                            },
                          ],
                          description: "The sort key and its direction",
                        },
                      },
                    },
                  },
                },
                required: ["name", "arguments"],
              },
              {
                type: "object",
                properties: {
                  name: {
                    type: "string",
                    const: "count",
                  },
                  arguments: {
                    type: "object",
                    properties: {
                      query: {
                        description:
                          "A filter/query parameter. Allows users to filter the documents to count. Matches the syntax of the filter argument of db.collection.count().",
                        type: "object",
                        properties: {},
                        additionalProperties: {},
                      },
                    },
                  },
                },
                required: ["name", "arguments"],
              },
            ],
          },
          description: "The method and its arguments to run",
        },
        verbosity: {
          default: "queryPlanner",
          description:
            "The verbosity of the explain plan, defaults to queryPlanner. If the user wants to know how fast is a query in execution time, use executionStats. It supports all verbosities as defined in the MongoDB Driver.",
          type: "string",
          enum: [
            "queryPlanner",
            "queryPlannerExtended",
            "executionStats",
            "allPlansExecution",
          ],
        },
      },
      required: ["connectionId", "database", "collection", "method"],
      additionalProperties: false,
    },
    annotations: {
      title: "explain",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "find",
    description: "Run a find query against a MongoDB collection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        filter: {
          description:
            "The query filter, matching the syntax of the query argument of db.collection.find()",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
        projection: {
          description:
            "The projection, matching the syntax of the projection argument of db.collection.find()",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
        limit: {
          default: 10,
          description: "The maximum number of documents to return",
          type: "number",
        },
        sort: {
          description:
            "A document, describing the sort order, matching the syntax of the sort argument of cursor.sort(). The keys of the object are the fields to sort on, while the values are the sort directions (1 for ascending, -1 for descending).",
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            anyOf: [
              {
                type: "number",
                const: 1,
              },
              {
                type: "number",
                const: -1,
              },
              {
                type: "string",
                const: "asc",
              },
              {
                type: "string",
                const: "desc",
              },
              {
                type: "string",
                const: "ascending",
              },
              {
                type: "string",
                const: "descending",
              },
              {
                type: "object",
                properties: {
                  $meta: {
                    type: "string",
                  },
                },
                required: ["$meta"],
              },
            ],
            description: "The sort key and its direction",
          },
        },
        responseBytesLimit: {
          default: 1048576,
          description:
            "The maximum number of bytes to return in the response. This value is capped by the server's configured maximum and cannot be exceeded.",
          type: "number",
        },
      },
      required: ["connectionId", "database", "collection"],
      additionalProperties: false,
    },
    annotations: {
      title: "find",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "insert-many",
    description:
      "Insert an array of documents into a MongoDB collection. If the list of documents is above com.mongodb/maxRequestPayloadBytes, consider inserting them in batches.",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        documents: {
          type: "array",
          items: {
            description: "An individual MongoDB document",
            type: "object",
            properties: {},
            additionalProperties: {},
          },
          description:
            "The array of documents to insert, matching the syntax of the document argument of db.collection.insertMany().",
        },
      },
      required: ["connectionId", "database", "collection", "documents"],
      additionalProperties: false,
    },
    annotations: {
      title: "insert-many",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "list-collections",
    description: "List all collections for a given database",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
      },
      required: ["connectionId", "database"],
      additionalProperties: false,
    },
    annotations: {
      title: "list-collections",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "list-databases",
    description: "List all databases for a MongoDB connection",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
      },
      required: ["connectionId"],
      additionalProperties: false,
    },
    annotations: {
      title: "list-databases",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "rename-collection",
    description: "Renames a collection in a MongoDB database",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        newName: {
          type: "string",
          description: "The new name for the collection",
        },
        dropTarget: {
          default: false,
          description: "If true, drops the target collection if it exists",
          type: "boolean",
        },
      },
      required: ["connectionId", "database", "collection", "newName"],
      additionalProperties: false,
    },
    annotations: {
      title: "rename-collection",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "update-many",
    description:
      "Updates all documents that match the specified filter for a collection. If the list of documents is above com.mongodb/maxRequestPayloadBytes, consider updating them in batches.",
    inputSchema: {
      type: "object",
      properties: {
        connectionId: {
          type: "string",
          description:
            "The connection to run the operation against. Use the id returned by one of the connect tools.",
        },
        database: {
          type: "string",
          description: "Database name",
        },
        collection: {
          type: "string",
          description: "Collection name",
        },
        filter: {
          description:
            "The selection criteria for the update, matching the syntax of the filter argument of db.collection.updateOne()",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
        update: {
          description:
            "An update document describing the modifications to apply using update operator expressions",
          type: "object",
          properties: {},
          additionalProperties: {},
        },
        upsert: {
          description:
            "Controls whether to insert a new document if no documents match the filter",
          type: "boolean",
        },
      },
      required: ["connectionId", "database", "collection", "update"],
      additionalProperties: false,
    },
    annotations: {
      title: "update-many",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "atlas-list-clusters",
    description: "List MongoDB Atlas clusters",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID to filter clusters",
        },
      },
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-list-clusters",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-list-projects",
    description: "List MongoDB Atlas projects.",
    inputSchema: {
      type: "object",
      properties: {
        orgId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas organization ID to filter projects. If not provided, projects for all orgs are returned.",
        },
        limit: {
          default: 10,
          description: "Max number of projects to return per page.",
          type: "integer",
          minimum: 1,
          maximum: 500,
        },
        pageNum: {
          default: 1,
          description: "Page number of projects to return.",
          type: "integer",
          minimum: 1,
          maximum: 9007199254740991,
        },
        includeCount: {
          default: false,
          description:
            "Whether to include the total number of matching projects. Note: enabling this makes the API request take much longer.",
          type: "boolean",
        },
      },
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-list-projects",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-inspect-cluster",
    description: "Inspect metadata of a MongoDB Atlas cluster",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Atlas cluster name",
        },
      },
      required: ["projectId", "clusterName"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-inspect-cluster",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-create-free-cluster",
    description: "Create a free MongoDB Atlas cluster",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID to create the cluster in",
        },
        name: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Name of the cluster",
        },
        region: {
          default: "US_EAST_1",
          type: "string",
          minLength: 1,
          maxLength: 50,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Region of the cluster",
        },
      },
      required: ["projectId", "name"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-create-free-cluster",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-create-access-list",
    description: "Allow Ip/CIDR ranges to access your MongoDB Atlas clusters.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID",
        },
        ipAddresses: {
          type: "array",
          items: {
            type: "string",
            format: "ipv4",
            pattern:
              "^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])$",
          },
          description: "IP addresses to allow access from",
        },
        cidrBlocks: {
          type: "array",
          items: {
            type: "string",
            format: "cidrv4",
            pattern:
              "^((25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\\.){3}(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]|[0-9])\\/([0-9]|[1-2][0-9]|3[0-2])$",
          },
          description: "CIDR blocks to allow access from",
        },
        comment: {
          default: "Added by MongoDB MCP Server to enable tool access",
          type: "string",
          maxLength: 80,
          pattern: "^[\\x20-\\x7E]*$",
          description: "Comment for the access list entries",
        },
      },
      required: ["projectId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-create-access-list",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-inspect-access-list",
    description:
      "Inspect Ip/CIDR ranges with access to your MongoDB Atlas clusters.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID",
        },
      },
      required: ["projectId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-inspect-access-list",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-list-db-users",
    description: "List MongoDB Atlas database users",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID to filter DB users",
        },
      },
      required: ["projectId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-list-db-users",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-create-db-user",
    description: "Create an MongoDB Atlas database user",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID",
        },
        username: {
          type: "string",
          minLength: 1,
          maxLength: 100,
          pattern: "^[a-zA-Z0-9._-]+$",
          description: "Username for the new user",
        },
        password: {
          description:
            "Password for the new user. IMPORTANT: If the user hasn't supplied an explicit password, leave it unset and under no circumstances try to generate a random one. A secure password will be generated by the MCP server if necessary.",
          type: "string",
          minLength: 1,
          maxLength: 100,
        },
        roles: {
          type: "array",
          items: {
            type: "object",
            properties: {
              roleName: {
                anyOf: [
                  {
                    type: "string",
                    enum: [
                      "atlasAdmin",
                      "backup",
                      "clusterMonitor",
                      "dbAdmin",
                      "dbAdminAnyDatabase",
                      "enableSharding",
                      "read",
                      "readAnyDatabase",
                      "readWrite",
                      "readWriteAnyDatabase",
                    ],
                  },
                  {
                    type: "string",
                    pattern: "^[a-zA-Z0-9_-]+$",
                  },
                ],
                description: "Role name",
              },
              databaseName: {
                default: "admin",
                type: "string",
                pattern: "^[\\x20-\\x7E]*$",
                description: "Database name",
              },
              collectionName: {
                type: "string",
                pattern: "^[\\x20-\\x7E]*$",
                description: "Collection name",
              },
            },
            required: ["roleName"],
          },
          description: "Roles for the new user",
        },
        clusters: {
          type: "array",
          items: {
            type: "string",
            minLength: 1,
            maxLength: 64,
            pattern: "^[a-zA-Z0-9_-]+$",
          },
          description:
            "Clusters to assign the user to, leave empty for access to all clusters",
        },
      },
      required: ["projectId", "username", "roles"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-create-db-user",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-create-project",
    description: "Create a MongoDB Atlas project",
    inputSchema: {
      type: "object",
      properties: {
        projectName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9\\s()@&+:._',-]+$",
          description: "Name for the new project",
        },
        orgId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Organization ID that will own the new project",
        },
      },
      required: ["projectName", "orgId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-create-project",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-list-orgs",
    description: "List MongoDB Atlas organizations",
    inputSchema: {
      type: "object",
      properties: {
        limit: {
          default: 10,
          description: "Max number of organizations to return per page.",
          type: "integer",
          minimum: 1,
          maximum: 500,
        },
        pageNum: {
          default: 1,
          description: "Page number of organizations to return.",
          type: "integer",
          minimum: 1,
          maximum: 9007199254740991,
        },
        includeCount: {
          default: false,
          description:
            "Whether to include the total number of matching organizations. Note: enabling this makes the API request take much longer.",
          type: "boolean",
        },
      },
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-list-orgs",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-list-alerts",
    description:
      "List triggered alerts for a MongoDB Atlas project. These are alerts Atlas has raised, not the alert configurations that define them. Defaults to OPEN alerts; set status to TRACKING or CLOSED to see others.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID to list alerts for",
        },
        status: {
          default: "OPEN",
          description:
            "Status of the alerts to return. Defaults to OPEN. TRACKING means the alert condition exists but hasn't persisted beyond the notification delay. OPEN means the alert condition currently exists. CLOSED means the alert has been resolved.",
          type: "string",
          enum: ["OPEN", "TRACKING", "CLOSED"],
        },
        limit: {
          default: 10,
          description: "Max results per page.",
          type: "integer",
          minimum: 1,
          maximum: 500,
        },
        pageNum: {
          default: 1,
          description: "Page number.",
          type: "integer",
          minimum: 1,
          maximum: 9007199254740991,
        },
        includeCount: {
          default: false,
          description:
            "Whether to include the total number of matching alerts. Defaults to false for faster responses.",
          type: "boolean",
        },
      },
      required: ["projectId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-list-alerts",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-get-performance-advisor",
    description:
      "Get MongoDB Atlas performance advisor recommendations and suggestions, which includes the operations: suggested indexes, drop index suggestions, schema suggestions, and a sample of the most recent (max 50) slow query logs",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID to get performance advisor recommendations. The project ID is a hexadecimal identifier of 24 characters. If the user has only specified the name, use the `atlas-list-projects` tool to retrieve the user's projects with their ids.",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description:
            "Atlas cluster name to get performance advisor recommendations",
        },
        operations: {
          default: [
            "suggestedIndexes",
            "dropIndexSuggestions",
            "slowQueryLogs",
            "schemaSuggestions",
          ],
          description: "Operations to get performance advisor recommendations",
          type: "array",
          items: {
            type: "string",
            enum: [
              "suggestedIndexes",
              "dropIndexSuggestions",
              "slowQueryLogs",
              "schemaSuggestions",
            ],
          },
        },
        since: {
          type: "string",
          format: "date-time",
          pattern:
            "^(?:(?:\\d\\d[2468][048]|\\d\\d[13579][26]|\\d\\d0[48]|[02468][048]00|[13579][26]00)-02-29|\\d{4}-(?:(?:0[13578]|1[02])-(?:0[1-9]|[12]\\d|3[01])|(?:0[469]|11)-(?:0[1-9]|[12]\\d|30)|(?:02)-(?:0[1-9]|1\\d|2[0-8])))T(?:(?:[01]\\d|2[0-3]):[0-5]\\d:[0-5]\\d(?:\\.\\d+)?(?:Z))$",
          description:
            "Date to get slow query logs since. Must be a string in ISO 8601 format. Only relevant for the slowQueryLogs operation.",
        },
        namespaces: {
          type: "array",
          items: {
            type: "string",
          },
          description:
            "Namespaces to get slow query logs. Only relevant for the slowQueryLogs operation.",
        },
      },
      required: ["projectId", "clusterName"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-get-performance-advisor",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-get-regions",
    description: "List supported MongoDB Atlas regions for a cloud provider.",
    inputSchema: {
      type: "object",
      properties: {
        provider: {
          type: "string",
          enum: ["AWS", "GCP", "AZURE"],
          description: "Cloud provider to list regions for.",
        },
      },
      required: ["provider"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-get-regions",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-load-sample-dataset",
    description:
      "Load a MongoDB sample dataset into an Atlas cluster, or check the status of a previously-initiated load. To start a new load, provide `clusterName` — the load runs asynchronously and the response includes a `jobId` and initial state. To check progress, call this tool again with `jobId` (sample dataset loads typically take 1–5 minutes). State can be WORKING, COMPLETED, or FAILED.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID that owns the cluster",
        },
        clusterName: {
          description:
            "Cluster name to load the sample dataset into. Provide this to initiate a new load. Mutually exclusive with jobId.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        jobId: {
          description:
            "Job id returned by a previous load request. Provide this to check the status of an in-progress load. Mutually exclusive with clusterName.",
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
        },
      },
      required: ["projectId"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-load-sample-dataset",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-create-cluster",
    description:
      "Create a MongoDB Atlas cluster (M10–M80, replica set or single shard, single or multi-region). Compute autoscaling is enabled by default: min instance size is set to the selected instance size, max is set two tiers above. Disk autoscaling is always enabled. For encryption at rest, the CMK provider must already have a valid configuration in the Atlas project. The tool returns immediately, use the atlas-inspect-cluster tool to poll the cluster state for readiness (state: IDLE). Connection strings are unavailable until the cluster reaches IDLE state. Note to LLM: Omit instance size unless specified by the user. If provider and regions are not already known, ask for the provider and desired locations together. Use atlas-get-regions to resolve natural-language locations or uncertain region codes before calling this tool.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID to create the cluster in. Use the atlas-list-projects to find project IDs if not provided.",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Name of the cluster.",
        },
        provider: {
          type: "string",
          enum: ["AWS", "GCP", "AZURE"],
          description: "Cloud provider for the cluster.",
        },
        regions: {
          minItems: 1,
          maxItems: 3,
          type: "array",
          items: {
            type: "string",
            minLength: 1,
            maxLength: 50,
            pattern: "^[a-zA-Z0-9_-]+$",
          },
          description:
            "Cloud provider regions in Atlas format using uppercase letters and underscores (e.g. US_EAST_1). The first region has the highest priority. Do not include duplicate regions.",
        },
        clusterType: {
          default: "REPLICASET",
          description:
            "Cluster topology. Use `SHARDED` for single-shard clusters, requires M30 or higher. Defaults to `REPLICASET`.",
          type: "string",
          enum: ["REPLICASET", "SHARDED"],
        },
        instanceSize: {
          description:
            "Instance size. NVME and high-memory instances are not supported. Minimum M30 when clusterType is SHARDED. Defaults to M10 for projects with fewer than 2 existing clusters, M30 otherwise. Omit unless explicitly specified by the user.",
          type: "string",
          enum: ["M10", "M20", "M30", "M40", "M50", "M60", "M80"],
        },
        computeAutoScaling: {
          default: true,
          description:
            "When true, enables compute autoscaling. Min instance size is set to the selected instance size, max is set two tiers above. Omit unless explicitly specified by the user.",
          type: "boolean",
        },
        diskSizeGB: {
          description:
            "Initial disk size in GB. Disk autoscaling is always enabled regardless of this value. Omit unless explicitly specified by the user.",
          type: "number",
          exclusiveMinimum: 0,
        },
        mongoDBVersion: {
          default: "LATEST",
          description:
            "MongoDB version to deploy. Use a pinned version for production environments where version stability is required. Defaults to `LATEST`.",
          type: "string",
          enum: ["7.0", "8.0", "LATEST"],
        },
        backup: {
          default: "SNAPSHOT",
          description:
            "`OFF`: no backups. `SNAPSHOT`: cloud backup snapshots, recommended for most workloads. `CONTINUOUS`: point-in-time restore, required for RPO-sensitive production workloads. Defaults to `SNAPSHOT`.",
          type: "string",
          enum: ["OFF", "SNAPSHOT", "CONTINUOUS"],
        },
        terminationProtectionEnabled: {
          default: false,
          description:
            "When true, prevents the cluster from being deleted until protection is explicitly disabled. Recommended for production clusters. Defaults to false.",
          type: "boolean",
        },
        encryptionAtRestProvider: {
          description:
            "Customer-managed key provider for encryption at rest. Defaults to the cluster's provider if a valid configuration exists. Use `NONE` to explicitly disable the feature. Omit unless explicitly specified by the user.",
          type: "string",
          enum: ["AWS", "AZURE", "GCP", "NONE"],
        },
      },
      required: ["projectId", "clusterName", "provider", "regions"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-create-cluster",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-pause-resume-cluster",
    description:
      "Pause or resume a dedicated (M10+) MongoDB Atlas cluster (Free and Flex clusters cannot be paused). Pause: paused clusters are unavailable for connections and do not incur compute costs. If the cluster being paused is the current active connection, it will be automatically disconnected. Returns an error if the cluster is already paused or not in a pausable state (must be IDLE). Resume: the cluster will not be immediately available after resuming. Use the atlas-inspect-cluster tool to poll the cluster state for readiness (state: IDLE). If the cluster is not paused, resuming it is a no-op.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID the cluster belongs to.",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Name of the cluster to pause or resume.",
        },
        action: {
          type: "string",
          enum: ["PAUSE", "RESUME"],
          description: "Action to perform on the cluster.",
        },
      },
      required: ["projectId", "clusterName", "action"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-pause-resume-cluster",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "atlas-streams-discover",
    description:
      "Discover and inspect Atlas Stream Processing resources. Also use for 'why is my processor failing', 'what workspaces do I have', 'show processor stats', or 'check processor health'. Use 'list-workspaces' to see all workspaces in a project. Use inspect actions for details on a specific resource. Use 'diagnose-processor' for a combined health report including state, stats, connection health, and recent errors. Use 'get-networking' for PrivateLink and account details.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID. Use atlas-list-projects to find project IDs if not available.",
        },
        action: {
          type: "string",
          enum: [
            "list-workspaces",
            "inspect-workspace",
            "list-connections",
            "inspect-connection",
            "list-processors",
            "inspect-processor",
            "diagnose-processor",
            "get-networking",
          ],
          description:
            "What to look up. Start with 'list-workspaces' to see available workspaces, then use inspect actions for details or 'diagnose-processor' for a health report.",
        },
        workspaceName: {
          description:
            "Workspace name. Required for all actions except 'list-workspaces' and 'get-networking'.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        resourceName: {
          description:
            "Connection or processor name. Required for 'inspect-connection', 'inspect-processor', and 'diagnose-processor'.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        responseFormat: {
          description:
            "Response detail level. 'concise' returns names and states only. 'detailed' returns full configuration and stats. Default: 'concise' for list actions, 'detailed' for inspect/diagnose.",
          type: "string",
          enum: ["concise", "detailed"],
        },
        cloudProvider: {
          description:
            "Cloud provider (AWS, AZURE, GCP). Only for 'get-networking': returns account details for the specified provider.",
          type: "string",
        },
        region: {
          description:
            "Cloud region. Only for 'get-networking': returns account details for the specified region.",
          type: "string",
        },
        limit: {
          description: "Max results per page for list actions. Default: 20.",
          type: "integer",
          minimum: 1,
          maximum: 100,
        },
        pageNum: {
          description: "Page number for list actions. Default: 1.",
          type: "integer",
          minimum: 1,
          maximum: 9007199254740991,
        },
      },
      required: ["projectId", "action"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-streams-discover",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
  {
    name: "atlas-streams-build",
    description:
      "Create Atlas Stream Processing resources. Use this tool for 'set up a Kafka pipeline', 'create a workspace', 'add a connection', or 'deploy a processor'. Use resource='workspace' to create a new workspace (specify cloud provider, region, and tier). Use resource='connection' to add a data source or sink to an existing workspace. Use resource='processor' to deploy a stream processor with a pipeline. Use resource='privatelink' to set up private networking. Typical workflow: create workspace → add connections → deploy processor.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID. Use atlas-list-projects to find project IDs if not available.",
        },
        resource: {
          type: "string",
          enum: ["workspace", "connection", "processor", "privatelink"],
          description:
            "What to create. Start with 'workspace', then 'connection', then 'processor'. Use 'privatelink' only if connections need private networking.",
        },
        workspaceName: {
          description:
            "Workspace name. Required for workspace, connection, and processor resources. Not required for privatelink (which is project-level). For 'workspace': the name to create. For others: the existing workspace to add to. Use `atlas-streams-discover` with action 'list-workspaces' to see existing workspaces.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        cloudProvider: {
          description: "Cloud provider. Required when resource='workspace'.",
          type: "string",
          enum: ["AWS", "GCP", "AZURE"],
        },
        region: {
          description:
            "Cloud region. Required when resource='workspace'. Use Atlas region names: AWS examples: 'VIRGINIA_USA', 'OREGON_USA', 'DUBLIN_IRL'. Azure examples: 'eastus2', 'westeurope'. GCP examples: 'US_CENTRAL1', 'EUROPE_WEST1'.",
          type: "string",
          minLength: 1,
          maxLength: 50,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        tier: {
          description:
            "Processing tier. Default: SP10. Only for resource='workspace'.",
          type: "string",
          enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
        },
        includeSampleData: {
          description:
            "Include the sample_stream_solar connection for testing. Default: true. Only for resource='workspace'.",
          type: "boolean",
        },
        connectionName: {
          description: "Connection name. Required when resource='connection'.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        connectionType: {
          description:
            "Connection type. Required when resource='connection'. Kafka: needs bootstrapServers, authentication, and security config. Use authentication.mechanism='AWS_MSK_IAM' with authentication.aws.roleArn for MSK IAM authentication. Cluster: needs clusterName and dbRoleToExecute. S3: needs aws.roleArn (must be registered via Atlas Cloud Provider Access). Https: needs url. AWSKinesisDataStreams: needs aws.roleArn (must be registered via Atlas Cloud Provider Access). AWSLambda: needs aws.roleArn (must be registered via Atlas Cloud Provider Access). SchemaRegistry: needs provider, schemaRegistryUrls, and authentication config. Sample: provides sample data for testing (no config needed).",
          type: "string",
          enum: [
            "Kafka",
            "Cluster",
            "S3",
            "Https",
            "AWSKinesisDataStreams",
            "AWSLambda",
            "SchemaRegistry",
            "Sample",
          ],
        },
        connectionConfig: {
          description:
            "Type-specific connection configuration. Only for resource='connection'. Omit entirely for connectionType='Sample' (no config needed). You may pass a partial config — the tool uses elicitation to collect missing required fields directly from the user.",
          type: "object",
          properties: {
            bootstrapServers: {
              description:
                "Comma-separated Kafka broker addresses (e.g. 'broker1:9092,broker2:9092'). Also accepts an array of strings, which will be joined with commas.",
              anyOf: [
                {
                  type: "string",
                },
                {
                  type: "array",
                  items: {
                    type: "string",
                  },
                },
              ],
            },
            authentication: {
              description: "Kafka authentication config.",
              type: "object",
              properties: {
                mechanism: {
                  type: "string",
                  enum: [
                    "PLAIN",
                    "SCRAM-256",
                    "SCRAM-512",
                    "OAUTHBEARER",
                    "AWS_MSK_IAM",
                  ],
                },
                username: {
                  type: "string",
                },
                password: {
                  type: "string",
                },
                aws: {
                  description:
                    "AWS configuration for AWS_MSK_IAM Kafka authentication.",
                  type: "object",
                  properties: {
                    roleArn: {
                      description:
                        "IAM role ARN registered via Atlas Cloud Provider Access.",
                      type: "string",
                    },
                  },
                  additionalProperties: {},
                },
              },
              additionalProperties: {},
            },
            security: {
              description: "Kafka security config.",
              type: "object",
              properties: {
                protocol: {
                  type: "string",
                  enum: ["SASL_SSL", "SASL_PLAINTEXT", "SSL"],
                },
              },
              additionalProperties: {},
            },
            clusterName: {
              description: "Atlas cluster name for Cluster connections.",
              type: "string",
            },
            dbRoleToExecute: {
              description:
                "Database role. Defaults to {role: 'readWriteAnyDatabase', type: 'BUILT_IN'}.",
              type: "object",
              properties: {
                role: {
                  type: "string",
                },
                type: {
                  default: "BUILT_IN",
                  type: "string",
                  enum: ["BUILT_IN", "CUSTOM"],
                },
              },
            },
            aws: {
              description:
                "AWS config for S3, Kinesis, and Lambda connections.",
              type: "object",
              properties: {
                roleArn: {
                  description:
                    "IAM role ARN registered via Atlas Cloud Provider Access.",
                  type: "string",
                },
                testBucket: {
                  description: "S3 test bucket name (optional, S3 only).",
                  type: "string",
                },
              },
              additionalProperties: {},
            },
            url: {
              description: "Webhook URL for Https connections.",
              type: "string",
            },
            headers: {
              description: "HTTP headers for Https connections.",
              type: "object",
              propertyNames: {
                type: "string",
              },
              additionalProperties: {
                type: "string",
              },
            },
            provider: {
              description:
                "Schema registry provider (e.g. 'CONFLUENT'). Only for SchemaRegistry connections. Defaults to 'CONFLUENT'.",
              type: "string",
            },
            schemaRegistryUrls: {
              description:
                "Schema registry URL(s) as an array of strings. Also accepts a single comma-separated string, which will be split into an array.",
              anyOf: [
                {
                  type: "array",
                  items: {
                    type: "string",
                  },
                },
                {
                  type: "string",
                },
              ],
            },
            schemaRegistryAuthentication: {
              description: "Schema registry auth. Defaults to USER_INFO.",
              type: "object",
              properties: {
                type: {
                  type: "string",
                  enum: ["USER_INFO", "SASL_INHERIT"],
                },
                username: {
                  type: "string",
                },
                password: {
                  type: "string",
                },
              },
              additionalProperties: {},
            },
            networking: {
              description:
                "Private networking config (PrivateLink or VPC peering). Kafka only.",
              type: "object",
              properties: {
                access: {
                  type: "object",
                  properties: {
                    type: {
                      type: "string",
                    },
                    connectionId: {
                      type: "string",
                    },
                  },
                  additionalProperties: {},
                },
              },
              additionalProperties: {},
            },
          },
          additionalProperties: {},
        },
        processorName: {
          description: "Processor name. Required when resource='processor'.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        pipeline: {
          description:
            "Pipeline stages for the stream processor. Required when resource='processor'. Must start with a $source stage and end with a sink stage ($merge, $emit, $iceberg, $https, or $externalFunction). Use $merge to write to Atlas cluster collections: {$merge: {into: {connectionName, db, coll}}}. Use $iceberg to write to Apache Iceberg tables on an S3 connection (must be the last stage): {$iceberg: {connectionName, bucket, databaseName, tableName, path}}. path is required and must not end with '/'. Requires tier SP10 or higher. On non-AWS workspaces also specify region. Use $emit to write to Kafka or Kinesis sinks: {$emit: {connectionName, topic}}. $emit only works with Kafka/Kinesis connections — do NOT use $emit with Https connections. Use $https to POST data to an Https connection: {$https: {connectionName}}. Use $externalFunction for Lambda: {$externalFunction: {connectionName, functionName, execution: 'async', as: 'result'}}. Lambda does NOT use $emit — use $externalFunction with execution='async' as a sink stage or execution='sync' for mid-pipeline enrichment. By default $https.onError is 'dlq', which requires a DLQ (see dlq parameter). Set {$https: {connectionName, onError: 'ignore'}} to skip DLQ. For Kafka $emit with Schema Registry: {$emit: {connectionName, topic, schemaRegistry: {connectionName: '<sr-connection>', valueSchema: {type: 'avro', schema: {<avro-schema>}, options: {subjectNameStrategy: 'TopicNameStrategy', autoRegisterSchemas: true}}}}}. Note: valueSchema.type must be lowercase 'avro'. valueSchema.schema (Avro schema definition) is always required even with autoRegisterSchemas. Kafka/Kinesis $source must include a 'topic'/'stream' field. Cluster $source must include db and coll: {$source: {connectionName, db, coll}}. $$NOW, $$ROOT, and $$CURRENT are not available in streaming request. Connections referenced in $source/$merge/$emit/$iceberg/$https/$externalFunction must already exist in the workspace.",
          type: "array",
          items: {
            type: "object",
            propertyNames: {
              type: "string",
            },
            additionalProperties: {},
          },
        },
        dlq: {
          description:
            "Dead letter queue configuration. Only for resource='processor'. Only include when the user explicitly requests a DLQ, or when the pipeline uses $https with default onError='dlq'. The DLQ connection must already exist in the workspace.",
          type: "object",
          properties: {
            connectionName: {
              type: "string",
              description: "Atlas connection name for DLQ output",
            },
            db: {
              type: "string",
              description: "Database name for DLQ collection",
            },
            coll: {
              type: "string",
              description: "Collection name for DLQ documents",
            },
          },
          required: ["connectionName", "db", "coll"],
        },
        processorTier: {
          description:
            "Baseline processing tier. Only for resource='processor'. Defaults to the workspace tier when omitted. If deployment fails with a tier error, the error names the minimum required tier — redeploy with processorTier set to that tier or higher.",
          type: "string",
          enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
        },
        autoscaling: {
          description:
            "Autoscaling configuration. Only for resource='processor'. Set enabled=true to enable it; omitted means disabled at creation. minTier and maxTier default to workspace bounds when enabled.",
          type: "object",
          properties: {
            enabled: {
              description:
                "Enable autoscaling. Explicit false disables autoscaling and clears its configuration.",
              type: "boolean",
            },
            minTier: {
              description:
                "Autoscaling floor. Null resets the floor to the workspace default tier.",
              anyOf: [
                {
                  type: "string",
                  enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
                },
                {
                  type: "null",
                },
              ],
            },
            maxTier: {
              description:
                "Autoscaling ceiling. Null resets the ceiling to the workspace maximum tier.",
              anyOf: [
                {
                  type: "string",
                  enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
                },
                {
                  type: "null",
                },
              ],
            },
          },
        },
        autoStart: {
          description:
            "Start the processor immediately after creation. Default: false. Only for resource='processor'. Omit unless the user explicitly asks to start the processor right away.",
          type: "boolean",
        },
        privateLinkConfig: {
          description:
            "PrivateLink configuration including provider and provider-specific fields. Required when resource='privatelink'.",
          type: "object",
          properties: {
            provider: {
              type: "string",
              enum: ["AWS", "GCP", "AZURE"],
              description:
                "Cloud provider for the PrivateLink endpoint. Required.",
            },
            region: {
              description:
                "Cloud region for the PrivateLink endpoint. Required for all vendors except AWS MSK. Use cloud-native region names: AWS 'us-east-1', Azure 'eastus2', GCP 'us-central1'.",
              type: "string",
            },
            vendor: {
              description:
                "PrivateLink vendor. If the user does not specify a vendor, ask them which vendor to use before proceeding. If they decline or say none, omit this field — the Atlas API will default to GENERIC. Valid values — AWS: 'CONFLUENT', 'MSK', 'KINESIS', 'S3'. Azure: 'EVENTHUB', 'CONFLUENT'. GCP: 'CONFLUENT'.",
              type: "string",
            },
            arn: {
              description:
                "Amazon Resource Name (ARN). Required for AWS MSK vendor.",
              type: "string",
            },
            authenticationScheme: {
              description:
                "Authentication scheme for an AWS MSK PrivateLink endpoint. Use 'IAM' for MSK IAM authentication, 'SASL_SCRAM' for SCRAM, or 'TLS' for mutual TLS.",
              type: "string",
              enum: ["TLS", "SASL_SCRAM", "IAM"],
            },
            dnsDomain: {
              description:
                "DNS domain hostname. Required for AWS CONFLUENT, AZURE EVENTHUB, AZURE CONFLUENT, and GCP CONFLUENT.",
              type: "string",
            },
            dnsSubDomain: {
              description:
                "DNS subdomains. Each value must contain the dnsDomain. Required for AWS CONFLUENT (use [] for serverless clusters).",
              type: "array",
              items: {
                type: "string",
              },
            },
            serviceEndpointId: {
              description:
                "Service endpoint ID. Required for AWS CONFLUENT, AWS S3, AWS KINESIS, and AZURE EVENTHUB. Do NOT provide for AZURE CONFLUENT or GCP CONFLUENT.",
              type: "string",
            },
            azureResourceIds: {
              description:
                "Azure Resource IDs. Required for AZURE CONFLUENT (instead of serviceEndpointId). Do NOT provide for AZURE EVENTHUB.",
              type: "array",
              items: {
                type: "string",
              },
            },
            gcpServiceAttachmentUris: {
              description:
                "GCP Private Service Connect attachment URIs. Required for GCP CONFLUENT.",
              type: "array",
              items: {
                type: "string",
              },
            },
          },
          required: ["provider"],
          additionalProperties: {},
        },
      },
      required: ["projectId", "resource"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-streams-build",
      openWorldHint: true,
      destructiveHint: false,
      readOnlyHint: false,
    },
  },
  {
    name: "atlas-streams-manage",
    description:
      "Manage Atlas Stream Processing resources: start/stop processors, modify pipelines, update configurations. Also use for 'change the pipeline', 'scale up my processor', or 'update my workspace tier'. Common workflow: action='stop-processor' → action='modify-processor' → action='start-processor'. Use `atlas-streams-discover` with action 'inspect-processor' to check state before managing.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID. Use atlas-list-projects to find project IDs if not available.",
        },
        workspaceName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Workspace name containing the resource to manage.",
        },
        action: {
          type: "string",
          enum: [
            "start-processor",
            "stop-processor",
            "modify-processor",
            "update-workspace",
            "update-connection",
            "accept-peering",
            "reject-peering",
          ],
          description:
            "Action to perform. One of: 'start-processor' — begin or resume processing (requires resourceName). 'stop-processor' — pause processing (requires resourceName). 'modify-processor' — change pipeline, DLQ, name, baseline tier, or autoscaling (requires resourceName and at least one changed field; processor must be stopped first). 'update-workspace' — change workspace tier or region. 'update-connection' — update connection config (requires resourceName). 'accept-peering' — accept a VPC peering request (requires peeringId, requesterAccountId, requesterVpcId). 'reject-peering' — reject a VPC peering request (requires peeringId). For peering actions, workspaceName can be any workspace in the project (peering is project-level).",
        },
        resourceName: {
          description:
            "Processor or connection name. Required for processor and connection actions.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        tier: {
          description:
            "Processing tier. For 'start-processor', sets the baseline tier for this run. For 'modify-processor', updates the persisted baseline tier. Must not exceed the workspace maximum tier.",
          type: "string",
          enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
        },
        autoscaling: {
          description:
            "Autoscaling configuration for 'start-processor' or 'modify-processor'. Omit to preserve persisted settings. Set enabled=false to disable and clear it. Omitted bounds preserve persisted values or use workspace defaults when first enabling.",
          type: "object",
          properties: {
            enabled: {
              description:
                "Enable autoscaling. Explicit false disables autoscaling and clears its configuration.",
              type: "boolean",
            },
            minTier: {
              description:
                "Autoscaling floor. Null resets the floor to the workspace default tier.",
              anyOf: [
                {
                  type: "string",
                  enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
                },
                {
                  type: "null",
                },
              ],
            },
            maxTier: {
              description:
                "Autoscaling ceiling. Null resets the ceiling to the workspace maximum tier.",
              anyOf: [
                {
                  type: "string",
                  enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
                },
                {
                  type: "null",
                },
              ],
            },
          },
        },
        resumeFromCheckpoint: {
          description:
            "Resume from last checkpoint on start. Default: true. Set false to reprocess from beginning (drops accumulated window state). Only for 'start-processor'.",
          type: "boolean",
        },
        startAtOperationTime: {
          description:
            "ISO 8601 timestamp to resume from. Only for 'start-processor'.",
          type: "string",
        },
        pipeline: {
          description:
            "New pipeline stages as an array of objects, e.g. [{$source: {connectionName: 'src'}}, {$match: {status: 'active'}}, {$merge: {into: {connectionName: 'dest', db: 'db', coll: 'coll'}}}]. Only for 'modify-processor'. Processor must be stopped first. If changing a window stage interval, the processor must be restarted with resumeFromCheckpoint=false.",
          type: "array",
          items: {
            type: "object",
            propertyNames: {
              type: "string",
            },
            additionalProperties: {},
          },
        },
        dlq: {
          description: "New DLQ configuration. Only for 'modify-processor'.",
          type: "object",
          properties: {
            connectionName: {
              type: "string",
            },
            db: {
              type: "string",
            },
            coll: {
              type: "string",
            },
          },
          required: ["connectionName", "db", "coll"],
        },
        newName: {
          description: "Rename processor. Only for 'modify-processor'.",
          type: "string",
        },
        newRegion: {
          description:
            "New region for workspace. Only for 'update-workspace'. Use Atlas region names (e.g. AWS: 'VIRGINIA_USA', Azure: 'eastus2', GCP: 'US_CENTRAL1').",
          type: "string",
        },
        newTier: {
          description:
            "New default tier for workspace. Only for 'update-workspace'.",
          type: "string",
          enum: ["SP2", "SP5", "SP10", "SP30", "SP50"],
        },
        connectionConfig: {
          description:
            "Updated connection configuration. Only for 'update-connection'. Provide only the fields to change. Note: networking config and connection type cannot be modified after creation — to change these, delete and recreate the connection.",
          type: "object",
          properties: {
            bootstrapServers: {
              description:
                "Comma-separated Kafka broker addresses (e.g. 'broker1:9092,broker2:9092'). Also accepts an array of strings, which will be joined with commas.",
              anyOf: [
                {
                  type: "string",
                },
                {
                  type: "array",
                  items: {
                    type: "string",
                  },
                },
              ],
            },
            authentication: {
              description: "Kafka authentication config.",
              type: "object",
              properties: {
                mechanism: {
                  type: "string",
                  enum: [
                    "PLAIN",
                    "SCRAM-256",
                    "SCRAM-512",
                    "OAUTHBEARER",
                    "AWS_MSK_IAM",
                  ],
                },
                username: {
                  type: "string",
                },
                password: {
                  type: "string",
                },
                aws: {
                  description:
                    "AWS configuration for AWS_MSK_IAM Kafka authentication.",
                  type: "object",
                  properties: {
                    roleArn: {
                      description:
                        "IAM role ARN registered via Atlas Cloud Provider Access.",
                      type: "string",
                    },
                  },
                  additionalProperties: {},
                },
              },
              additionalProperties: {},
            },
            security: {
              description: "Kafka security config.",
              type: "object",
              properties: {
                protocol: {
                  type: "string",
                  enum: ["SASL_SSL", "SASL_PLAINTEXT", "SSL"],
                },
              },
              additionalProperties: {},
            },
            clusterName: {
              description: "Atlas cluster name for Cluster connections.",
              type: "string",
            },
            dbRoleToExecute: {
              description:
                "Database role. Defaults to {role: 'readWriteAnyDatabase', type: 'BUILT_IN'}.",
              type: "object",
              properties: {
                role: {
                  type: "string",
                },
                type: {
                  default: "BUILT_IN",
                  type: "string",
                  enum: ["BUILT_IN", "CUSTOM"],
                },
              },
            },
            aws: {
              description:
                "AWS config for S3, Kinesis, and Lambda connections.",
              type: "object",
              properties: {
                roleArn: {
                  description:
                    "IAM role ARN registered via Atlas Cloud Provider Access.",
                  type: "string",
                },
                testBucket: {
                  description: "S3 test bucket name (optional, S3 only).",
                  type: "string",
                },
              },
              additionalProperties: {},
            },
            url: {
              description: "Webhook URL for Https connections.",
              type: "string",
            },
            headers: {
              description: "HTTP headers for Https connections.",
              type: "object",
              propertyNames: {
                type: "string",
              },
              additionalProperties: {
                type: "string",
              },
            },
            provider: {
              description:
                "Schema registry provider (e.g. 'CONFLUENT'). Only for SchemaRegistry connections. Defaults to 'CONFLUENT'.",
              type: "string",
            },
            schemaRegistryUrls: {
              description:
                "Schema registry URL(s) as an array of strings. Also accepts a single comma-separated string, which will be split into an array.",
              anyOf: [
                {
                  type: "array",
                  items: {
                    type: "string",
                  },
                },
                {
                  type: "string",
                },
              ],
            },
            schemaRegistryAuthentication: {
              description: "Schema registry auth. Defaults to USER_INFO.",
              type: "object",
              properties: {
                type: {
                  type: "string",
                  enum: ["USER_INFO", "SASL_INHERIT"],
                },
                username: {
                  type: "string",
                },
                password: {
                  type: "string",
                },
              },
              additionalProperties: {},
            },
            networking: {
              description:
                "Private networking config (PrivateLink or VPC peering). Kafka only.",
              type: "object",
              properties: {
                access: {
                  type: "object",
                  properties: {
                    type: {
                      type: "string",
                    },
                    connectionId: {
                      type: "string",
                    },
                  },
                  additionalProperties: {},
                },
              },
              additionalProperties: {},
            },
          },
          additionalProperties: {},
        },
        peeringId: {
          description:
            "VPC peering connection ID. Required for 'accept-peering' and 'reject-peering'.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        requesterAccountId: {
          description:
            "AWS account ID of the peering requester. Required for 'accept-peering'.",
          type: "string",
        },
        requesterVpcId: {
          description:
            "VPC ID of the peering requester. Required for 'accept-peering'.",
          type: "string",
        },
      },
      required: ["projectId", "workspaceName", "action"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-streams-manage",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "atlas-streams-teardown",
    description:
      "Delete Atlas Stream Processing resources. Also use for 'remove my workspace', 'disconnect a source', 'delete all processors', or 'clean up my streams environment'. Performs basic safety checks before deletion: summarizes counts of processors and connections, highlights connections referenced by processors where possible, and surfaces API errors if processors are still running when deletion is attempted. Use `atlas-streams-discover` to review resources before deleting.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description:
            "Atlas project ID. Use atlas-list-projects to find project IDs if not available.",
        },
        resource: {
          type: "string",
          enum: [
            "processor",
            "connection",
            "workspace",
            "privatelink",
            "peering",
          ],
          description:
            "What to delete. 'processor': stop first recommended. 'connection': ensure no processor references it. 'workspace': removes all contained connections and processors.",
        },
        workspaceName: {
          description:
            "Workspace name. Required for workspace, connection, and processor deletion.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
        resourceName: {
          description: "Name or ID of the specific resource to delete.",
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
      },
      required: ["projectId", "resource"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-streams-teardown",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "atlas-upgrade-cluster",
    description:
      "Upgrade or scale a MongoDB Atlas cluster. Free and Flex clusters can be upgraded to Flex or M10 Dedicated. Dedicated clusters can be scaled to a different instance size, and compute autoscaling settings can be updated. When scaling a Dedicated cluster, at least one of targetTier, computeAutoScaling, minInstanceSize, or maxInstanceSize must be provided. Compute autoscaling defaults to enabled when upgrading to M10 Dedicated: min instance size is set to the selected instance size, max is set two tiers above, unless overridden. Note to LLM: If provider and region are not already known, ask for both together in a single question before calling this tool. Use atlas-get-regions to resolve natural-language locations or uncertain region codes before calling this tool.",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
          description: "Atlas project ID",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
          description: "Name of the cluster to upgrade",
        },
        targetTier: {
          description:
            "For a Free/Flex source cluster: the target tier to upgrade to, defaults to FLEX for Free clusters, M10 for Flex clusters. For a Dedicated cluster: the new instance size (M10-M80) to scale it to.",
          type: "string",
          enum: ["FLEX", "M10", "M20", "M30", "M40", "M50", "M60", "M80"],
        },
        computeAutoScaling: {
          description:
            "Enable/disable compute autoscaling, for a Dedicated cluster or a Free/Flex-to-M10 upgrade. Omit unless explicitly specified by the user.",
          type: "boolean",
        },
        minInstanceSize: {
          description:
            "Minimum instance size (M10-M80) for compute autoscaling, for a Dedicated cluster or a Free/Flex-to-M10 upgrade. Omit unless explicitly specified by the user.",
          type: "string",
          enum: ["M10", "M20", "M30", "M40", "M50", "M60", "M80"],
        },
        maxInstanceSize: {
          description:
            "Maximum instance size (M10-M200) for compute autoscaling, for a Dedicated cluster or a Free/Flex-to-M10 upgrade. Omit unless explicitly specified by the user.",
          type: "string",
          enum: [
            "M10",
            "M20",
            "M30",
            "M40",
            "M50",
            "M60",
            "M80",
            "M140",
            "M200",
          ],
        },
        provider: {
          description:
            "Cloud provider (e.g. AWS, GCP, AZURE) for a Free/Flex source cluster. Preserves the existing value if omitted. Does not apply if a cluster is already Dedicated.",
          type: "string",
          pattern: "^[A-Z_]+$",
        },
        region: {
          description:
            "Cloud provider region in Atlas format using uppercase letters and underscores (e.g. US_EAST_1) for a Free/Flex source cluster. Preserves the existing value if omitted. Does not apply if a cluster is already Dedicated.",
          type: "string",
          minLength: 1,
          maxLength: 50,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
      },
      required: ["projectId", "clusterName"],
      additionalProperties: false,
    },
    annotations: {
      title: "atlas-upgrade-cluster",
      openWorldHint: true,
      readOnlyHint: false,
      destructiveHint: true,
    },
  },
  {
    name: "remote-atlas-connect",
    description: "Connect to MongoDB Atlas cluster",
    inputSchema: {
      type: "object",
      properties: {
        projectId: {
          type: "string",
          minLength: 24,
          maxLength: 24,
          pattern: "^[0-9a-fA-F]+$",
        },
        clusterName: {
          type: "string",
          minLength: 1,
          maxLength: 64,
          pattern: "^[a-zA-Z0-9_-]+$",
        },
      },
      required: ["projectId", "clusterName"],
      additionalProperties: false,
    },
    annotations: {
      title: "remote-atlas-connect",
      openWorldHint: true,
      readOnlyHint: true,
      destructiveHint: false,
    },
  },
];
